import { useBidHistoryQuery } from './useGetBidHistory';

export interface IBidWinnerRecognitionResult {
  /** The office's most recent winning cycle, or null if it has never won. */
  currentCycleWin: {
    cycleId: string;
    cycleName: string;
    cycleEndDate: string;
    /** Award year for the "<year> Winner" banner copy. The server populates it
     * (awards-service#35); still typed optional in the @tempmee/awards contract, so guard it. */
    cycleYear?: number;
  } | null;
  /** Whether the office has ever won any cycle. */
  isEverWinner: boolean;
  isLoading: boolean;
}

/**
 * Winner recognition for the selected office, DERIVED from the member history
 * (`GET /cycles`) — awards-service has no dedicated winner endpoint, and the
 * history read already carries everything needed:
 *
 * - `currentCycleWin` = the newest cycle whose effective `membershipStatus` is
 *   `winner`. The server keeps a win in history for good (winner is never
 *   folded to not-selected); the banner itself clears at `cycleEndDate`, so
 *   this returns the win and lets `BidWinnerBanner` decide when to stop
 *   showing it. A current win is on the newest-first first page.
 * - `isEverWinner` = the page-level `toolkitEligible` ("has ever won",
 *   computed by the server across every page).
 *
 * Shares the page-1 history query/cache, so it adds no fetch when the menu
 * gate or BID page has already loaded it. Defaults to the "no win" shape while
 * loading or on error.
 */
export const useBidWinnerRecognition = (enabled = true): IBidWinnerRecognitionResult => {
  const query = useBidHistoryQuery(1, enabled);
  const winnerRow = query.data?.items.find((cycle) => cycle.membershipStatus === 'winner');

  return {
    currentCycleWin: winnerRow
      ? {
          cycleId: winnerRow.cycleId,
          cycleName: winnerRow.cycleName,
          cycleEndDate: winnerRow.cycleEndDate,
          cycleYear: winnerRow.cycleYear,
        }
      : null,
    isEverWinner: query.data?.toolkitEligible ?? false,
    isLoading: query.isLoading,
  };
};
